/* 
Registers our routes.

All routes should point to a component from our "views" folder.
Each route can have the following metadata:
- mustBeSignedIn: whether the user must be signed in to access the route
- layout: the layout to use; if left off App.vue will pick an appropriate layout, but for readability we try to include a layout for every route
- title: title to display in the browser; this will also go into the user's browsing history 
- permitWhenChangingPassword [default: false]: whether this route can be navigated to when the user is required to change their password
- transitionName [default: "fade"]: animation transition to use when navigating to this route
- mustBeSetup [default: false]: whether the levy collection must be setup before navigating to this route
*/
import ChangePassword from "@/views/ChangePassword.vue";
import LevyEstimator from "@/views/LevyEstimator.vue";
import PublicLevyEstimator from "@/views/PublicLevyEstimator.vue";
import ExceptionLogDetail from "@/views/exception-logs/Detail.vue";
import ExceptionLogs from "@/views/listings/ExceptionLogs.vue";
import Home from "@/views/Home.vue";
import InvoiceDetail from "@/views/invoices/Detail.vue";
import InvoiceHistory from "@/views/invoices/History.vue";
import InvoicePdf from "@/views/invoices/Pdf.vue";
import InvoicePdfPreview from "@/views/invoices/PdfPreview.vue";
import Invoices from "@/views/listings/Invoices.vue";
import LevyCollectionDashboard from "@/views/levy-collections/Dashboard.vue";
import LevyCollectionSandbox from "@/views/levy-collections/Sandbox.vue";
import LevyCollectionDeletionRedirect from "@/views/levy-collections/DeletionRedirect.vue";
import LevyCollectionHistory from "@/views/levy-collections/History.vue";
import LevyCollectionInvoices from "@/views/levy-collections/Invoices.vue";
import LevyCollectionNotices from "@/views/levy-collections/Notices.vue";
import LevyCollectionOverrides from "@/views/levy-collections/Overrides.vue";
import LevyCollectionProviderCalculation from "@/views/levy-collection-providers/Calculation.vue";
import LevyCollectionProviderContacts from "@/views/levy-collection-providers/Contacts.vue";
import LevyCollectionProviderHistory from "@/views/levy-collection-providers/History.vue";
import LevyCollectionProviderInvoice from "@/views/levy-collection-providers/Invoice.vue";
import LevyCollectionProviderNotices from "@/views/levy-collection-providers/Notices.vue";
import LevyCollectionProviderNotes from "@/views/levy-collection-providers/Notes.vue";
import LevyCollectionProviders from "@/views/levy-collections/Providers.vue";
import LevyCollections from "@/views/listings/LevyCollections.vue";
import LevyCollectionSetupLevyCalculation from "@/views/levy-collection-setup/LevyCalculation.vue";
import LevyCollectionSetupLevyEstimator from "@/views/levy-collection-setup/LevyEstimator.vue";
import LevyCollectionSetupNotices from "@/views/levy-collection-setup/Notices.vue";
import LevyCollectionSetupInvoicing from "@/views/levy-collection-setup/Invoicing.vue";
import LevyCollectionSetupInvoiceTemplatePdfPreview from "@/views/levy-collection-setup/InvoiceTemplatePdfPreview.vue";
import LevyCollectionWorkflow from "@/views/levy-collections/Workflow.vue";
import Notices from "@/views/listings/Notices.vue";
import NoticeTemplateDetails from "@/views/notice-templates/Details.vue";
import NoticeTemplateHistory from "@/views/notice-templates/History.vue";
import NoticeTemplatePreview from "@/views/notice-templates/Preview.vue";
import ProviderContacts from "@/views/providers/Contacts.vue";
import ProviderHistory from "@/views/providers/History.vue";
import ProviderInvoices from "@/views/providers/Invoices.vue";
import ProviderNotices from "@/views/providers/Notices.vue";
import ProviderNotes from "@/views/providers/Notes.vue";
import Providers from "@/views/listings/Providers.vue";
import ProviderSummary from "@/views/providers/Summary.vue";
import ProviderRiskProfile from "@/views/providers/RiskProfile.vue";
import RoleHistory from "@/views/roles/History.vue";
import Roles from "@/views/listings/Roles.vue";
import RoleSummary from "@/views/roles/Summary.vue";
import ScheduledJobHistory from "@/views/scheduled-jobs/History.vue";
import ScheduledJobs from "@/views/listings/ScheduledJobs.vue";
import ScheduledJobSummary from "@/views/scheduled-jobs/Summary.vue";
import SignIn from "@/views/SignIn.vue";
import MfaSignInAttempt from "@/views/MfaSignInAttempt.vue";
import MyProfile from "@/views/users/MyProfile.vue";

import SignOutRedirect from "@/views/SignOutRedirect.vue";
import SystemLogs from "@/views/listings/SystemLogs.vue";
import TestingTools from "@/views/TestingTools.vue";
import UserActivity from "@/views/users/Activity.vue";
import Users from "@/views/listings/Users.vue";
import UserSummary from "@/views/users/Summary.vue";
import AuthenticationLogs from "@/views/listings/AuthenticationLogs.vue";
import store from "@/store";
import Router from "vue-router";
import { SIGN_IN_SET_REDIRECT } from "@/store/users/mutation-types";
import { SIGN_IN_CLEAR_PASSWORD } from "@/store/users/mutation-types";
import { GLOBAL_ERROR_HIDE } from "@/store/system/mutation-types";

const anonymous = "layout-for-anonymous";
const staff = "layout-for-staff";
const redirection = "layout-for-redirection";
const estimator = "layout-for-levy-estimator";

export const router = new Router({
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      component: Home,
      meta: { mustBeSignedIn: true, layout: staff, title: "Home" }
    },
    {
      path: "/sign-in",
      component: SignIn,
      meta: { mustNotBeSignedIn: true, layout: anonymous, title: "Sign in" }
    },
    {
      path: "/mfa/attempt-challenge",
      component: MfaSignInAttempt,
      meta: {mustNotBeSignedIn: true, layout: anonymous, title: "MFA Attempt Challenge" }
    },
    {
      path: "/sign-out",
      component: SignOutRedirect,
      meta: { mustBeSignedIn: true, permitWhenChangingPassword: true, layout: redirection, title: "Signing out" }
    },
    {
      path: "/levy-estimator/:key",
      component: LevyEstimator,
      meta: { layout: estimator, title: "Provider levy estimator", metaTags: [{ name: "robots", content: "noindex" }] }
    },
    {
      path: "/public-levy-estimator/:type/:year",
      component: PublicLevyEstimator,
      meta: { layout: estimator, title: "Public levy estimator" }
    },
    {
      path: "/change-password",
      component: ChangePassword,
      meta: { mustBeSignedIn: true, permitWhenChangingPassword: true, layout: anonymous, title: "Change password" }
    },
    {
      path: "/exception-logs",
      component: ExceptionLogs,
      meta: { mustBeSignedIn: true, layout: staff, title: "Exception logs" }
    },
    {
      path: "/exception-logs/:id",
      component: ExceptionLogDetail,
      meta: { mustBeSignedIn: true, layout: staff, title: "Exception log" }
    },
    {
      path: "/invoices",
      component: Invoices,
      meta: { mustBeSignedIn: true, layout: staff, title: "Invoices" }
    },
    {
      path: "/invoices/:id",
      redirect: "/invoices/:id/detail"
    },
    {
      path: "/invoices/:id/detail",
      name: "invoice-detail",
      component: InvoiceDetail,
      meta: { mustBeSignedIn: true, layout: staff, title: "Detail - Invoice", transitionName: "none" }
    },
    {
      path: "/invoices/:id/pdf",
      name: "invoice-pdf",
      component: InvoicePdf,
      meta: { mustBeSignedIn: true, layout: staff, title: "PDF - Invoice", transitionName: "none" }
    },
    {
      path: "/invoices/:id/history",
      name: "invoice-history",
      component: InvoiceHistory,
      meta: { mustBeSignedIn: true, layout: staff, title: "History - Invoice", transitionName: "none" }
    },
    {
      path: "/levy-collections/:levyCollectionId/invoices/:id",
      redirect: "/levy-collections/:levyCollectionId/invoices/:id/detail"
    },
    {
      path: "/levy-collections/:levyCollectionId/invoices/:id/detail",
      name: "levy-collection-invoice-detail",
      component: InvoiceDetail,
      meta: { mustBeSignedIn: true, layout: staff, title: "Detail - Invoice", transitionName: "none" }
    },
    {
      path: "/levy-collections/:levyCollectionId/invoices/:id/pdf",
      name: "levy-collection-invoice-pdf",
      component: InvoicePdf,
      meta: { mustBeSignedIn: true, layout: staff, title: "PDF - Invoice", transitionName: "none" }
    },
    {
      path: "/levy-collections/:levyCollectionId/invoices/:id/history",
      name: "levy-collection-invoice-history",
      component: InvoiceHistory,
      meta: { mustBeSignedIn: true, layout: staff, title: "History - Invoice", transitionName: "none" }
    },
    {
      path: "/providers/:providerId/invoices/:id",
      redirect: "/providers/:providerId/invoices/:id/detail"
    },
    {
      path: "/providers/:providerId/invoices/:id/detail",
      name: "provider-invoice-detail",
      component: InvoiceDetail,
      meta: { mustBeSignedIn: true, layout: staff, title: "Detail - Invoice", transitionName: "none" }
    },
    {
      path: "/providers/:providerId/invoices/:id/pdf",
      name: "provider-invoice-pdf",
      component: InvoicePdf,
      meta: { mustBeSignedIn: true, layout: staff, title: "PDF - Invoice", transitionName: "none" }
    },
    {
      path: "/providers/:providerId/invoices/:id/history",
      name: "provider-invoice-history",
      component: InvoiceHistory,
      meta: { mustBeSignedIn: true, layout: staff, title: "History - Invoice", transitionName: "none" }
    },
    {
      path: "/levy-collections",
      component: LevyCollections,
      meta: { mustBeSignedIn: true, layout: staff, title: "Levy collections" }
    },
    {
      path: "/levy-collections/:id",
      redirect: "/levy-collections/:id/dashboard"
    },
    {
      path: "/levy-collections/:id/delete",
      component: LevyCollectionDeletionRedirect,
      meta: { mustBeSignedIn: true, layout: redirection }
    },
    {
      path: "/levy-collections/:id/dashboard",
      component: LevyCollectionDashboard,
      meta: { mustBeSignedIn: true, mustBeSetup: true, layout: staff, title: "Dashboard - Levy collection" }
    },
    {
      path: "/levy-collections/:id/sandbox",
      component: LevyCollectionSandbox,
      meta: { mustBeSignedIn: true, layout: staff, title: "Sandbox - Levy collection" }
    },
    {
      path: "/levy-collections/:id/workflow",
      component: LevyCollectionWorkflow,
      meta: { mustBeSignedIn: true, mustBeSetup: true, layout: staff, title: "Workflow - Levy collection" }
    },
    {
      path: "/levy-collections/:id/providers",
      component: LevyCollectionProviders,
      meta: { mustBeSignedIn: true, mustBeSetup: true, layout: staff, title: "Providers - Levy collection" }
    },
    {
      path: "/levy-collections/:id/overrides",
      component: LevyCollectionOverrides,
      meta: { mustBeSignedIn: true, mustBeSetup: true, layout: staff, title: "Overrides - Levy collection" }
    },
    {
      path: "/levy-collections/:id/notices",
      component: LevyCollectionNotices,
      meta: { mustBeSignedIn: true, mustBeSetup: true, layout: staff, title: "Notices - Levy collection" }
    },
    {
      path: "/levy-collections/:id/invoices",
      component: LevyCollectionInvoices,
      meta: { mustBeSignedIn: true, mustBeSetup: true, layout: staff, title: "Invoices - Levy collection" }
    },
    {
      path: "/levy-collections/:id/history",
      component: LevyCollectionHistory,
      meta: { mustBeSignedIn: true, mustBeSetup: true, layout: staff, title: "History - Levy collection" }
    },
    {
      path: "/levy-collections/:id/setup",
      redirect: "/levy-collections/:id/setup/levy-calculation"
    },
    {
      path: "/levy-collections/:id/setup/levy-calculation",
      component: LevyCollectionSetupLevyCalculation,
      meta: {
        mustBeSignedIn: true,
        layout: staff,
        title: "Levy calculation - Setup - Levy collection",
        transitionName: "none"
      }
    },
    {
      path: "/levy-collections/:id/setup/levy-estimator",
      component: LevyCollectionSetupLevyEstimator,
      meta: {
        mustBeSignedIn: true,
        layout: staff,
        title: "Estimator templates - Setup - Levy collection",
        transitionName: "none"
      }
    },
    {
      path: "/levy-collections/:id/setup/notices",
      component: LevyCollectionSetupNotices,
      meta: {
        mustBeSignedIn: true,
        layout: staff,
        title: "Notices - Setup - Levy collection",
        transitionName: "none"
      }
    },
    {
      path: "/levy-collections/:id/setup/invoicing",
      component: LevyCollectionSetupInvoicing,
      meta: {
        mustBeSignedIn: true,
        layout: staff,
        title: "Invoice template - Setup - Levy collection",
        transitionName: "none"
      }
    },
    {
      path: "/levy-collections/:levyCollectionId/invoice-template-preview",
      component: LevyCollectionSetupInvoiceTemplatePdfPreview,
      meta: { mustBeSignedIn: true, layout: redirection }
    },
    {
      path: "/levy-collections/:levyCollectionId/providers/:providerId",
      redirect: "/levy-collections/:levyCollectionId/providers/:providerId/calculation"
    },
    {
      path: "/levy-collections/:levyCollectionId/providers/:providerId/calculation",
      name: "levy-collection-provider-calculation",
      component: LevyCollectionProviderCalculation,
      meta: {
        mustBeSignedIn: true,
        layout: staff,
        title: "Levy calculation - Levy collection provider",
        transitionName: "none"
      }
    },
    {
      path: "/levy-collections/:levyCollectionId/providers/:providerId/invoice",
      name: "levy-collection-provider-invoice",
      component: LevyCollectionProviderInvoice,
      meta: { mustBeSignedIn: true, layout: staff, title: "Invoice - Levy collection provider", transitionName: "none" }
    },
    {
      path: "/levy-collections/:levyCollectionId/providers/:providerId/notices",
      name: "levy-collection-provider-notices",
      component: LevyCollectionProviderNotices,
      meta: { mustBeSignedIn: true, layout: staff, title: "Notices - Levy collection provider", transitionName: "none" }
    },
    {
      path: "/levy-collections/:levyCollectionId/providers/:providerId/notes",
      name: "levy-collection-provider-notes",
      component: LevyCollectionProviderNotes,
      meta: { mustBeSignedIn: true, layout: staff, title: "Notes - Levy collection provider", transitionName: "none" }
    },
    {
      path: "/levy-collections/:levyCollectionId/providers/:providerId/contacts",
      name: "levy-collection-provider-contacts",
      component: LevyCollectionProviderContacts,
      meta: {
        mustBeSignedIn: true,
        layout: staff,
        title: "Contacts - Levy collection provider",
        transitionName: "none"
      }
    },
    {
      path: "/levy-collections/:levyCollectionId/providers/:providerId/history",
      name: "levy-collection-provider-history",
      component: LevyCollectionProviderHistory,
      meta: { mustBeSignedIn: true, layout: staff, title: "History - Levy collection provider", transitionName: "none" }
    },
    {
      path: "/levy-collections/:levyCollectionId/providers/:providerId/pdf-preview",
      component: InvoicePdfPreview,
      meta: { mustBeSignedIn: true, layout: redirection }
    },
    {
      path: "/levy-collections/:levyCollectionId/setup/notices/:noticeType",
      redirect: "/levy-collections/:levyCollectionId/setup/notices/:noticeType/preview"
    },
    {
      path: "/levy-collections/:levyCollectionId/setup/notices/:noticeType/preview",
      name: "notice-template-preview",
      component: NoticeTemplatePreview,
      meta: {
        mustBeSignedIn: true,
        layout: staff,
        title: "Preview - Notice template - Setup - Levy collection",
        transitionName: "none"
      }
    },
    {
      path: "/levy-collections/:levyCollectionId/setup/notices/:noticeType/details",
      name: "notice-template-details",
      component: NoticeTemplateDetails,
      meta: {
        mustBeSignedIn: true,
        layout: staff,
        title: "Details - Notice template - Setup - Levy collection",
        transitionName: "none"
      }
    },
    {
      path: "/levy-collections/:levyCollectionId/setup/notices/:noticeType/history",
      name: "notice-template-history",
      component: NoticeTemplateHistory,
      meta: {
        mustBeSignedIn: true,
        layout: staff,
        title: "History - Notice template - Setup - Levy collection",
        transitionName: "none"
      }
    },
    {
      path: "/notices",
      component: Notices,
      meta: { mustBeSignedIn: true, layout: staff, title: "Notices" }
    },
    {
      path: "/providers",
      component: Providers,
      meta: { mustBeSignedIn: true, layout: staff, title: "Providers" }
    },
    {
      path: "/providers/:id",
      redirect: "/providers/:id/summary"
    },
    {
      path: "/providers/:providerId/summary",
      component: ProviderSummary,
      meta: { mustBeSignedIn: true, layout: staff, title: "Summary - Provider", transitionName: "none" }
    },
    {
      path: "/providers/:providerId/risk-profile",
      component: ProviderRiskProfile,
      meta: { mustBeSignedIn: true, layout: staff, title: "Risk profile - Provider", transitionName: "none" }
    },
    {
      path: "/providers/:providerId/contacts",
      component: ProviderContacts,
      meta: { mustBeSignedIn: true, layout: staff, title: "Contacts - Provider", transitionName: "none" }
    },
    {
      path: "/providers/:providerId/notices",
      component: ProviderNotices,
      meta: { mustBeSignedIn: true, layout: staff, title: "Notices - Provider", transitionName: "none" }
    },
    {
      path: "/providers/:providerId/notes",
      component: ProviderNotes,
      meta: { mustBeSignedIn: true, layout: staff, title: "Notes - Provider", transitionName: "none" }
    },
    {
      path: "/providers/:providerId/invoices",
      component: ProviderInvoices,
      meta: { mustBeSignedIn: true, layout: staff, title: "Invoices - Provider", transitionName: "none" }
    },
    {
      path: "/providers/:providerId/history",
      component: ProviderHistory,
      meta: { mustBeSignedIn: true, layout: staff, title: "History - Provider", transitionName: "none" }
    },
    {
      path: "/roles",
      component: Roles,
      meta: { mustBeSignedIn: true, layout: staff, title: "Roles" }
    },
    {
      path: "/roles/:id",
      redirect: "/roles/:id/summary"
    },
    {
      path: "/roles/:id/summary",
      component: RoleSummary,
      meta: { mustBeSignedIn: true, layout: staff, title: "Summary - Role", transitionName: "none" }
    },
    {
      path: "/roles/:id/history",
      component: RoleHistory,
      meta: { mustBeSignedIn: true, layout: staff, title: "History - Role", transitionName: "none" }
    },
    {
      path: "/scheduled-jobs",
      component: ScheduledJobs,
      meta: { mustBeSignedIn: true, layout: staff, title: "Scheduled jobs" }
    },
    {
      path: "/scheduled-jobs/:id",
      redirect: "/scheduled-jobs/:id/summary"
    },
    {
      path: "/scheduled-jobs/:id/summary",
      component: ScheduledJobSummary,
      meta: { mustBeSignedIn: true, layout: staff, title: "Summary - Scheduled job", transitionName: "none" }
    },
    {
      path: "/scheduled-jobs/:id/history",
      component: ScheduledJobHistory,
      meta: { mustBeSignedIn: true, layout: staff, title: "History - Scheduled job", transitionName: "none" }
    },
    {
      path: "/system-logs",
      component: SystemLogs,
      meta: { mustBeSignedIn: true, layout: staff, title: "System logs" }
    },
    {
      path: "/testing-tools-anonymous",
      component: TestingTools,
      meta: { mustNotBeSignedIn: true, layout: anonymous, title: "Testing tools" }
    },
    {
      path: "/testing-tools",
      component: TestingTools,
      meta: { mustBeSignedIn: true, layout: staff, title: "Testing tools" }
    },
    {
      path: "/users",
      component: Users,
      meta: { mustBeSignedIn: true, layout: staff, title: "Users" }
    },
    {
      path: "/users/:id",
      redirect: "/users/:id/summary"
    },
    {
      path: "/users/:id/activity",
      component: UserActivity,
      meta: { mustBeSignedIn: true, layout: staff, title: "Activity - User", transitionName: "none" }
    },
    {
      path: "/users/:id/summary",
      component: UserSummary,
      meta: { mustBeSignedIn: true, layout: staff, title: "Summary - User", transitionName: "none" }
    },
    {
      path: "/user/profile",
      component: MyProfile,
      meta: { mustBeSignedIn: true, layout: staff, title: "My profile" }
    },
    {
      path: "/authentication-logs",
      component: AuthenticationLogs,
      meta: { mustBeSignedIn: true, layout: staff, title: "Authentication Logs" }
    }
  ]
});

router.beforeEach((to, from, next) => {
  // https://alligator.io/vuejs/vue-router-modify-head/
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.title);
  document.title = nearestWithTitle ? nearestWithTitle.meta.title + " - LTPS" : "LTPS";
  Array.from(document.querySelectorAll("[data-vue-router-controlled]")).map(el => el.parentNode.removeChild(el));
  const routeInfo = to.matched.find(r => r.meta && r.meta.metaTags);
  if (routeInfo) {
    routeInfo.meta.metaTags
      .map(d => {
        const tag = document.createElement("meta");
        Object.keys(d).forEach(key => {
          tag.setAttribute(key, d[key]);
        });
        tag.setAttribute("data-vue-router-controlled", "");
        return tag;
      })
      .forEach(tag => document.head.appendChild(tag));
  }
const isSignedIn = store.state.users.user.isSignedIn;
  const mustChangePassword = isSignedIn && store.state.users.user.details.mustChangePassword;
  if (store.state.system.globalError.show) {
    store.commit(GLOBAL_ERROR_HIDE);
  }
  if (isSignedIn && mustChangePassword && !to.matched.some(r => r.meta.permitWhenChangingPassword)) {
    return next("/change-password");
  } else if (to.matched.some(record => record.meta.mustBeSignedIn)) {
    if (!isSignedIn) {
      store.commit(SIGN_IN_SET_REDIRECT, to.path);
      return next("/sign-in");
    } else if (to.matched.some(record => record.meta.mustBeSetup)) {
      const levyCollectionId = to.params.id;
      if (store.state.lookups.loaded) {
        const lc = store.state.lookups.data.currentLevyCollections.find(c => c.id == levyCollectionId);
        if (lc && !lc.isSetup) {
          return next(`/levy-collections/${levyCollectionId}/setup`);
        }
      }
      return next();
    } else {
      next();
    }
  } else if (to.matched.some(record => record.meta.mustNotBeSignedIn)) {
    if (isSignedIn) {
      return next("/");
    } else {
      next();
    }
  } else {
    next();
  }
});

router.afterEach((_, from) => {
  if (from.path == "/sign-in") {
    store.commit(SIGN_IN_CLEAR_PASSWORD);
  }
});
